// components/ProductSelection/index.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../common/Button";
import Badge from "../../common/Badge";
import FullScreenModal from "./FullScreenModal";
import FilterSidebar from "./FilterSidebar";
import EnhancedProductCard from "./ProductCard";
import ProductSearchInterface from "./ProductSearchInterface";
import {
  notifyError,
  notifySuccess,
  notifyInfo,
} from "../../../utils/notification";
import { getAllProductsThunk } from "../../../store/thunks/adminProductThunks";
import { getAllCategoriesThunk } from "../../../store/thunks/categoryThunks";
import { parseSearchQuery, initializeFilters } from "./utils/productHelpers";
import {
  SearchIcon,
  ViewGridIcon,
  ViewListIcon,
  CubeIcon,
  PlusIcon,
  CheckIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/outline";

const ProductSelection = ({
  isOpen = false,
  onClose,
  onProductSelected,
  mode = "browse",
  title = "Select Products",

  // Feature flags
  allowSearch = true,
  allowCreate = true,
  allowEdit = true,
  showPricing = true,
  multiSelect = false,
  showCheckbox = true,
  autoApplyFilters = true,

  // Navigation
  enableNavigation = true,
  createRoute = "/products/new",
  editRoute = "/products/edit",

  // Filtering
  filterByBranch = true,
  filterByStatus = "ACTIVE",
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchTimeoutRef = useRef(null);
  const browseTimeoutRef = useRef(null);

  // Redux state
  const { products, loading } = useSelector((state) => state.adminProduct);
  const { categories } = useSelector((state) => state.category);
  const { user } = useSelector((state) => state.auth);
  const { selectedBranch } = useSelector((state) => state.rate.ui);

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestion, setSearchSuggestion] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [filters, setFilters] = useState(() =>
    initializeFilters(filterByBranch ? selectedBranch : null, filterByStatus)
  );

  // Load categories
  useEffect(() => {
    dispatch(getAllCategoriesThunk());
  }, [dispatch]);

  // Initialize - Load products on open
  useEffect(() => {
    if (isOpen && mode === "browse") {
      handleBrowseWithFilters(filters, true);
    }
  }, [isOpen]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (browseTimeoutRef.current) {
        clearTimeout(browseTimeoutRef.current);
      }
    };
  }, []);

  // Handle browse - FIXED: Accept filters as parameter
  const handleBrowseWithFilters = useCallback(
    (filtersToUse, immediate = false) => {
      const executeBrowse = () => {
        const browseParams = {
          ...filtersToUse,
          limit: 100,
          includePricing: showPricing ? "true" : "false",
          pricingBranches: selectedBranch || undefined,
        };

        // Clean up empty filter values
        Object.keys(browseParams).forEach((key) => {
          if (
            browseParams[key] === "" ||
            browseParams[key] === null ||
            browseParams[key] === undefined ||
            (Array.isArray(browseParams[key]) && browseParams[key].length === 0)
          ) {
            delete browseParams[key];
          }
        });

        console.log("Browsing with params:", browseParams); // Debug log

        dispatch(getAllProductsThunk(browseParams))
          .unwrap()
          .then(() => setHasSearched(true))
          .catch((error) => {
            console.error("Browse error:", error);
            notifyError("Failed to load products");
          });
      };

      if (immediate) {
        executeBrowse();
      } else {
        // Clear existing timeout
        if (browseTimeoutRef.current) {
          clearTimeout(browseTimeoutRef.current);
        }
        // Set new timeout
        browseTimeoutRef.current = setTimeout(executeBrowse, 300);
      }
    },
    [dispatch, showPricing, selectedBranch]
  );

  // Handle search
  const performSearch = useCallback(
    (query) => {
      if (query.length >= 2) {
        setIsSearching(true);
        setHasSearched(true);

        const searchParams = {
          search: query,
          status: filterByStatus,
          limit: 100,
          includePricing: showPricing ? "true" : "false",
          pricingBranches: selectedBranch || undefined,
        };

        if (filterByBranch && selectedBranch) {
          searchParams.branchCode = selectedBranch;
        }

        dispatch(getAllProductsThunk(searchParams))
          .unwrap()
          .then(() => setIsSearching(false))
          .catch((error) => {
            console.error("Search error:", error);
            setIsSearching(false);
            notifyError("Failed to search products");
          });
      }
    },
    [dispatch, filterByBranch, selectedBranch, filterByStatus, showPricing]
  );

  // Handle search change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length > 2) {
      const suggestion = parseSearchQuery(query);
      setSearchSuggestion(suggestion);
    } else {
      setSearchSuggestion(null);
    }

    if (query.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setHasSearched(false);
      setIsSearching(false);
      // If search is cleared, go back to browse mode
      if (mode === "browse") {
        handleBrowseWithFilters(filters, true);
      }
    }
  };

  // Handle filter change - FIXED: Pass new filters directly to browse
  const handleFilterChange = (newFilters) => {
    console.log("Filter changed:", newFilters); // Debug log
    setFilters(newFilters);
  };

  // Handle apply from sidebar - FIXED: Use current filters
  const handleApplyFilters = (currentFilters) => {
    console.log("Applying filters:", currentFilters); // Debug log
    handleBrowseWithFilters(currentFilters, false);
  };

  // Handle product selection
  const handleSelectProduct = (product) => {
    if (multiSelect) {
      setSelectedProducts((prev) => {
        const exists = prev.find((p) => p._id === product._id);
        if (exists) {
          return prev.filter((p) => p._id !== product._id);
        }
        return [...prev, product];
      });
    } else {
      if (onProductSelected) {
        onProductSelected(product);
        handleClose();
      }
    }
  };

  // Handle create
  const handleCreateProduct = () => {
    if (enableNavigation) {
      navigate(createRoute);
      handleClose();
    } else {
      notifyInfo("Navigation disabled");
    }
  };

  // Handle edit
  const handleEditProduct = (productId) => {
    if (enableNavigation) {
      navigate(`${editRoute}/${productId}`);
      handleClose();
    } else {
      notifyInfo("Navigation disabled");
    }
  };

  // Handle close
  const handleClose = () => {
    setSearchQuery("");
    setHasSearched(false);
    setSearchSuggestion(null);
    setSelectedProducts([]);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (browseTimeoutRef.current) {
      clearTimeout(browseTimeoutRef.current);
    }
    onClose();
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts([...products]);
    }
  };

  // Handle confirm selection
  const handleConfirmSelection = () => {
    if (onProductSelected) {
      onProductSelected(multiSelect ? selectedProducts : selectedProducts[0]);
    }
    handleClose();
  };

  // Handle reset filters
  const handleResetFilters = () => {
    const resetFilters = initializeFilters(selectedBranch, filterByStatus);
    setFilters(resetFilters);
    handleBrowseWithFilters(resetFilters, true);
  };

  // Calculate pricing summary
  const pricingSummary = useMemo(() => {
    if (!showPricing || selectedProducts.length === 0) return null;

    const totalItems = selectedProducts.length;
    const totalWeight = selectedProducts.reduce(
      (sum, p) => sum + (p.netWeight || 0),
      0
    );
    const prices = selectedProducts
      .map((p) => p.pricing?.summary?.lowestPrice)
      .filter(Boolean);
    const totalPrice = prices.reduce((sum, price) => sum + price, 0);

    return {
      totalItems,
      totalWeight,
      totalPrice,
      averagePrice: prices.length > 0 ? totalPrice / prices.length : 0,
    };
  }, [selectedProducts, showPricing]);

  // Display products
  const displayProducts = products || [];

  // Modal title
  const modalTitle = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        {selectedProducts.length > 0 && multiSelect && (
          <Badge variant="primary" size="lg">
            {selectedProducts.length} selected
          </Badge>
        )}
        {autoApplyFilters && (
          <Badge variant="success" size="sm">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Live Filters
            </div>
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-3">
        {/* View Mode Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-2 rounded transition-all ${
              viewMode === "grid"
                ? "bg-white shadow text-primary"
                : "text-gray-600"
            }`}
            title="Grid View"
          >
            <ViewGridIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded transition-all ${
              viewMode === "list"
                ? "bg-white shadow text-primary"
                : "text-gray-600"
            }`}
            title="List View"
          >
            <ViewListIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Create Button */}
        {allowCreate && (
          <Button size="sm" onClick={handleCreateProduct}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Product
          </Button>
        )}
      </div>
    </div>
  );

  // Modal actions
  const modalActions = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-4">
        {multiSelect && displayProducts.length > 0 && (
          <Button variant="secondary" size="sm" onClick={handleSelectAll}>
            {selectedProducts.length === displayProducts.length
              ? "Deselect All"
              : "Select All"}
          </Button>
        )}

        {/* Pricing Summary */}
        {pricingSummary && (
          <div className="flex items-center gap-6 px-4 py-2 bg-primary-50 rounded-lg">
            <div>
              <p className="text-xs text-gray-600">Items</p>
              <p className="text-lg font-bold text-gray-900">
                {pricingSummary.totalItems}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Weight</p>
              <p className="text-lg font-bold text-gray-900">
                {pricingSummary.totalWeight.toFixed(2)}g
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Total Price</p>
              <p className="text-xl font-bold text-primary flex items-center">
                <CurrencyRupeeIcon className="w-5 h-5" />
                {pricingSummary.totalPrice.toLocaleString("en-IN", {
                  maximumFractionDigits: 0,
                })}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {multiSelect && selectedProducts.length > 0 && (
          <Button
            variant="primary"
            onClick={handleConfirmSelection}
            className="min-w-[200px]"
          >
            <CheckIcon className="w-5 h-5 mr-2" />
            Confirm Selection ({selectedProducts.length})
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      actions={modalActions}
    >
      <div className="flex h-full">
        {/* Filters Sidebar - FIXED: Pass handleApplyFilters */}
        <FilterSidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          categories={categories}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          resultCount={displayProducts.length}
          autoApply={autoApplyFilters}
          debounceDelay={500}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Search Bar */}
          {allowSearch && (
            <div className="flex-shrink-0 p-6 bg-gray-50 border-b border-gray-200">
              <ProductSearchInterface
                searchQuery={searchQuery}
                searchSuggestion={searchSuggestion}
                isSearching={isSearching}
                onSearchChange={handleSearchChange}
                onCreateProduct={handleCreateProduct}
                allowCreate={false}
              />
            </div>
          )}

          {/* Products Grid/List */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {loading || isSearching ? (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading products...</p>
                  {autoApplyFilters && (
                    <p className="text-sm text-gray-500 mt-2">
                      Applying filters...
                    </p>
                  )}
                </div>
              </div>
            ) : displayProducts.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <CubeIcon className="w-24 h-24 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    No products found
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchQuery
                      ? `No results for "${searchQuery}"`
                      : "Try adjusting your filters"}
                  </p>
                  <div className="flex gap-3 justify-center">
                    {searchQuery && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setSearchQuery("");
                          handleBrowseWithFilters(filters, true);
                        }}
                      >
                        Clear Search
                      </Button>
                    )}
                    <Button variant="secondary" onClick={handleResetFilters}>
                      Reset Filters
                    </Button>
                    {allowCreate && (
                      <Button onClick={handleCreateProduct}>
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Create New Product
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6"
                    : "space-y-4"
                }
              >
                {displayProducts.map((product) => (
                  <EnhancedProductCard
                    key={product._id}
                    product={product}
                    viewMode={viewMode}
                    isSelected={selectedProducts.some(
                      (p) => p._id === product._id
                    )}
                    onSelect={() => handleSelectProduct(product)}
                    onEdit={
                      allowEdit ? () => handleEditProduct(product._id) : null
                    }
                    showPricing={showPricing}
                    multiSelect={multiSelect}
                    showCheckbox={showCheckbox}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
};

export default ProductSelection;
